<template> 
  <div v-if="showNavbar">
    <!-- Snackbar for Logout Notification -->
    <v-snackbar
      v-model="snackbar.show"
      :timeout="3000"
      :color="snackbar.color"
      location="top"
      class="snackbar"
    >
      {{ snackbar.message }}
    </v-snackbar>

    <!-- Navigation Drawer -->
    <v-navigation-drawer
      v-model="drawer"
      location="start"
      :permanent="false"
      color="#002060"
      width="240"
      id="navigationDrawer"
      style="border-radius: 0px 50px 50px 0px"
      class="drawer-background"
    >
      <!-- Logo Section -->
      <div class="drawer-logo pa-10" style="text-align: center; margin: 0; background-color: white; padding: 20px 10px; border-radius: 0 50px 0 0;">
        <v-img
       src="@/assets/logo2.png"
          alt="Logo"
          style="max-height: 120px; max-width: 200px;"
        />
      </div>

      <!-- Navigation List -->
      <v-list nav dense>
        <template v-for="(item, index) in filteredNavigationItems" :key="item.path || item.title">
          <!-- Custom Law Questions Dropdown -->
          <div v-if="item.title === 'Law Questions'" class="custom-dropdown">
            <!-- Law Questions Parent Item -->
            <v-list-item
              :class="[
                'nav-item custom-qa-item',
                {
                  'selected-item': $route.path.startsWith('/qa') && !$route.path.includes('/qa-assignment')
                }
              ]"
              @click="onClickLawQuestions"
              :aria-label="`Toggle ${item.title} submenu`"
              role="button"
              :id="`navItem-${index}`"
            >
              <v-list-item-title class="nav-title">
                <v-icon class="icon">{{ item.icon }}</v-icon>
                {{ item.title }}
                <v-icon
                  class="custom-dropdown-arrow"
                  :class="{ 'rotated': qaDropdownOpen }"
                >
                  mdi-chevron-down
                </v-icon>
              </v-list-item-title>
            </v-list-item>

            <!-- Custom Submenu Items -->
            <div
              class="custom-submenu"
              :class="{ 'submenu-open': qaDropdownOpen }"
            >
              <v-list-item
                class="nav-item sub-item"
                @click="navigateToQA('pending')"
                :aria-label="`Go to Pending Questions`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-clock-outline</v-icon>
                  Pending Questions
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                class="nav-item sub-item"
                @click="navigateToQA('completed')"
                :aria-label="`Go to Completed Questions`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-check-circle-outline</v-icon>
                  Completed Questions
                </v-list-item-title>
              </v-list-item>
            </div>
          </div>

          <!-- Custom Clients Dropdown -->
          <div v-else-if="item.title === 'Clients'" class="custom-dropdown">
            <!-- Clients Parent Item -->
            <v-list-item
              :class="[
                'nav-item custom-clients-item',
                {
                  'selected-item': $route.path.startsWith('/clients') || $route.path.startsWith('/add-client')
                }
              ]"
              @click="onClickClients"
              :aria-label="`Toggle ${item.title} submenu`"
              role="button"
              :id="`navItem-${index}`"
            >
              <v-list-item-title class="nav-title">
                <v-icon class="icon">{{ item.icon }}</v-icon>
                {{ item.title }}
                <v-icon
                  class="custom-dropdown-arrow"
                  :class="{ 'rotated': clientsDropdownOpen }"
                >
                  mdi-chevron-down
                </v-icon>
              </v-list-item-title>
            </v-list-item>

            <!-- Custom Submenu Items -->
            <div
              class="custom-submenu"
              :class="{ 'submenu-open': clientsDropdownOpen }"
            >
              <v-list-item
                class="nav-item sub-item"
                @click="navigateToClients('individual')"
                :aria-label="`Go to Individual Clients`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-account</v-icon>
                  Individual Clients
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                class="nav-item sub-item"
                @click="navigateToClients('business')"
                :aria-label="`Go to Companies`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-domain</v-icon>
                  Companies
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                class="nav-item sub-item"
                @click="navigateToClients('trust')"
                :aria-label="`Go to Trusts`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-shield-account</v-icon>
                  Trusts
                </v-list-item-title>
              </v-list-item>
            </div>
          </div>

          <!-- Custom Membership Dropdown -->
          <div v-else-if="item.title === 'Members'" class="custom-dropdown">
            <!-- Membership Parent Item -->
            <v-list-item
              :class="[
                'nav-item custom-membership-item',
                {
                  'selected-item': $route.path.startsWith('/members')
                }
              ]"
              @click="onClickMembership"
              :aria-label="`Toggle ${item.title} submenu`"
              role="button"
              :id="`navItem-${index}`"
            >
              <v-list-item-title class="nav-title">
                <v-icon class="icon">{{ item.icon }}</v-icon>
                {{ item.title }}
                <v-icon
                  class="custom-dropdown-arrow"
                  :class="{ 'rotated': membershipDropdownOpen }"
                >
                  mdi-chevron-down
                </v-icon>
              </v-list-item-title>
            </v-list-item>

            <!-- Custom Submenu Items -->
            <div
              class="custom-submenu"
              :class="{ 'submenu-open': membershipDropdownOpen }"
            >
              <v-list-item
                class="nav-item sub-item"
                @click="navigateToMembership('pending-invite')"
                :aria-label="`Go to Invite/Pending Members`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-email-outline</v-icon>
                  Invite/Pending Members
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                class="nav-item sub-item"
                @click="navigateToMembership('individual')"
                :aria-label="`Go to Individual Members`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-account</v-icon>
                  Individual Members
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                class="nav-item sub-item"
                @click="navigateToMembership('business')"
                :aria-label="`Go to Business Members`"
              >
                <v-list-item-title class="nav-title sub-title">
                  <v-icon class="icon">mdi-domain</v-icon>
                  Business Members
                </v-list-item-title>
              </v-list-item>
            </div>
          </div>

          <!-- Regular Navigation Items -->
          <v-list-item
            v-else
            :class="[
              'nav-item',
              {
                'selected-item':
                  item.path === $route.path ||
                  (item.title === 'Law Questions Assignment' && $route.path.includes('/qa-assignment'))
              }
            ]"
            @click="selectItem(item)"
            :aria-label="`Navigate to ${item.title}`"
            :id="`navItem-${index}`"
          >
            <v-list-item-title class="nav-title">
              <v-icon class="icon">{{ item.icon }}</v-icon>
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- App Bar -->
    <v-app-bar
      density="comfortable"
      height="70"
      elevation="0"
      color="#f2f2f2"
      class="app-bar"
    >
      <v-app-bar-nav-icon
        @click="toggleDrawer"
        aria-label="Toggle Navigation Drawer"
        id="navIcon"
      ></v-app-bar-nav-icon>

      <div class="toolbar-title-container" :class="{ 'underline-active': underlineActive }" style="flex: 1;">
        <h2 class="toolbar-title" id="pageTitle">{{ currentPageTitle }}</h2>
        <div class="underline"></div>
      </div>

      <!-- User Information -->
      <div class="user-info" id="userInfo" style="display: flex; align-items: center;">
        <h3 v-if="isDesktop" class="user-info__name" style="margin-right: 8px;">
          {{ displayName }}
        </h3>

        <v-menu
          v-model="showUserMenu"
          offset-y
          :close-on-content-click="false"
          activator="parent"
        >
          <template #activator="{ props }">
            <v-btn
              v-bind="props"
              variant="text"
              class="mx-5"
              icon
              aria-label="User Menu"
              id="userMenuButton"
            >
              <v-avatar size="40" color="#002060">
                <span class="avatar-text">{{ userInitials }}</span>
              </v-avatar>
            </v-btn>
          </template>

          <v-card>
            <v-card-text class="text-center">
              <div class="user-menu__avatar">
                <v-avatar size="48" color="#002060">
                  <span class="avatar-text">{{ userInitials }}</span>
                </v-avatar>
              </div>
              <h3 class="user-menu__name">{{ displayName }}</h3>
              <p class="user-menu__role">{{ userRole }}</p>
              <v-divider class="my-3"></v-divider>
              <v-btn
                class="menu-btn"
                variant="text"
                @click="goToProfile"
                aria-label="Go to Profile"
                block
              >
                Profile
              </v-btn>
              <v-divider class="my-3"></v-divider>
              <v-btn
                class="menu-btn logout-btn"
                variant="text"
                @click="logout"
                aria-label="Logout"
                :loading="isLoggingOut"
                :disabled="isLoggingOut"
                block
              >
                {{ isLoggingOut ? 'Logging out...' : 'Logout' }}
              </v-btn>
            </v-card-text>
          </v-card>
        </v-menu>
      </div>
    </v-app-bar>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ActivityLogger } from '../utils/activityLogger';

export default {
  name: "NavBar",
  data() {
    return {
      drawer: true,
      underlineActive: false,
      currentPageTitle: "DMM-LAW",
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
      isTourActive: false,
      showUserMenu: false,
      excludedRoutes: ["/", "/forgotPassword", "/details"],
      qaDropdownOpen: false,
      clientsDropdownOpen: false,
      membershipDropdownOpen: false,
      isLoggingOut: false,
      sessionStatus: {
        isActive: false,
        remainingTime: 10
      }
    };
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'user', 'userRole', 'isAdmin', 'isManager', 'isMember']),

    // Role-based computed properties
    isExecutive() {
      return this.userRole === 'Executive';
    },
    isEmployee() {
      return this.userRole === 'Employee';
    },
    isQAAgent() {
      return this.userRole === 'QA Agent';
    },

    isDesktop() {
      return window.innerWidth >= 960;
    },

    displayName() {
      if (this.user && this.user.displayName) {
        return this.user.displayName;
      }
      return this.user?.email?.split('@')[0] || 'User';
    },

    userInitials() {
      if (this.user && this.user.displayName) {
        const names = this.user.displayName.split(' ');
        return names.map(name => name[0]).join('').toUpperCase();
      }
      return this.user?.email?.[0]?.toUpperCase() || 'U';
    },

    navigationItems() {
      const baseItems = [];

      // Add role-specific navigation items
      if (this.isAdmin || this.isExecutive) {
        // Admin and Executive have full access to everything
        baseItems.push(
          { title: "Dashboard", icon: "mdi-home", path: "/dashboard" },
          { title: "User Management", icon: "mdi-account-group", path: "/user" },
          { title: "Members", icon: "mdi-chart-timeline-variant", path: "/members" },
          { title: "Clients", icon: "mdi-history", path: "/clients" },
          { title: "Liv-Life Users", icon: "mdi-account-multiple", path: "/livlife-users" },
          { title: "Law Questions", icon: "mdi-clipboard-check", path: "/qa" },
          { title: "Activity Log", icon: "mdi-clipboard-list", path: "/activity" }
        );
      } else if (this.userRole === 'Employee') {
        // Employee can access dashboard, clients and Law Questions
        baseItems.push(
          { title: "Dashboard", icon: "mdi-home", path: "/dashboard" },
          { title: "Clients", icon: "mdi-history", path: "/clients" },
          { title: "Law Questions", icon: "mdi-clipboard-check", path: "/qa" }
        );
      } else if (this.userRole === 'QA Agent') {
        // QA Agent can only access Law Questions
        baseItems.push(
          { title: "Law Questions", icon: "mdi-clipboard-check", path: "/qa" }
        );
      } else if (this.isMember) {
        baseItems.push(
          { title: "Dashboard", icon: "mdi-home", path: "/dashboard" },
          { title: "Law Questions", icon: "mdi-clipboard-check", path: "/qa" }
        );
      } else {
        // Regular users
        baseItems.push(
          { title: "Dashboard", icon: "mdi-home", path: "/dashboard" },
          { title: "Law Questions", icon: "mdi-clipboard-check", path: "/qa" }
        );
      }

      return baseItems;
    },

    filteredNavigationItems() {
      return this.navigationItems;
    },

    showNavbar() {
      return this.isAuthenticated && !this.excludedRoutes.includes(this.$route.path);
    },
  },
  watch: {
    "$route.path"() {
      this.updatePageTitle();
      this.triggerUnderlineAnimation();
      this.syncDropdownStates(); // keep dropdowns exclusive & in sync with route
    },
  },
  methods: {
    toggleDrawer() {
      this.drawer = !this.drawer;
    },

    selectItem(item) {
      if (item.path) {
        this.$router.push(item.path);
      } else if (item.action && typeof this[item.action] === "function") {
        this[item.action]();
      }
    },

    // Parent item clicks now ONLY toggle dropdowns; NO redirect
    onClickLawQuestions() {
      this.qaDropdownOpen = !this.qaDropdownOpen;
      this.clientsDropdownOpen = false; // keep exclusive
      // intentionally no routing here
    },

    onClickClients() {
      this.clientsDropdownOpen = !this.clientsDropdownOpen;
      this.qaDropdownOpen = false; // keep exclusive
      this.membershipDropdownOpen = false; // keep exclusive
      // intentionally no routing here
    },

    onClickMembership() {
      this.membershipDropdownOpen = !this.membershipDropdownOpen;
      this.qaDropdownOpen = false; // keep exclusive
      this.clientsDropdownOpen = false; // keep exclusive
      // intentionally no routing here
    },

    // Legacy toggles (kept in case you use them elsewhere)
    toggleQaDropdown() {
      this.qaDropdownOpen = !this.qaDropdownOpen;
      if (this.qaDropdownOpen) this.clientsDropdownOpen = false;
    },

    toggleClientsDropdown() {
      this.clientsDropdownOpen = !this.clientsDropdownOpen;
      if (this.clientsDropdownOpen) this.qaDropdownOpen = false;
    },

    navigateToQA(type) {
      this.$router.push({ path: '/qa', query: { type } });
    },

    navigateToClients(type) {
      // You can route to a filtered list or separate pages by type via query
      this.$router.push({ path: '/clients', query: { type } });
    },

    navigateToMembership(type) {
      // Route to members page with different views based on type
      this.$router.push({ path: '/members', query: { type } });
    },

    goToProfile() {
      // Close the menu and navigate to profile
      this.showUserMenu = false;
      this.$router.push('/profile');
    },

    async logout() {
      // Prevent multiple logout attempts
      if (this.isLoggingOut) return;

      this.isLoggingOut = true;

      // Close the user menu
      this.showUserMenu = false;

      try {
        // Log logout activity before logging out
        const displayName = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown User';
        await ActivityLogger.logout(displayName, this.user?.uid);

        // First logout from store (this will clear the auth state)
        await this.$store.dispatch('logout');

        // Then redirect to login page after auth is cleared
        this.$router.push("/");

      } catch (error) {
        console.error('Logout error:', error);
        // Even if there's an error, still logout and redirect
        await this.$store.dispatch('logout');
        this.$router.push("/");
      } finally {
        this.isLoggingOut = false;
      }
    },

    updatePageTitle() {
      // Dynamically set the page title based on the current route
      const path = this.$route.path;
      
      // Handle special cases for dropdown items
      if (path.startsWith('/qa')) {
        this.currentPageTitle = 'Law Questions';
      } else if (path.startsWith('/clients') || path.startsWith('/add-client')) {
        this.currentPageTitle = 'Clients';
      } else {
        // Find exact match for other routes
        const activeItem = this.navigationItems.find(
          item => item.path === path
        );
        this.currentPageTitle = activeItem ? activeItem.title : "DMM-LAW";
      }
    },

    triggerUnderlineAnimation() {
      this.underlineActive = false;
      setTimeout(() => (this.underlineActive = true), 100);
    },

    startTour() {
      if (this.isTourActive) return;
      this.isTourActive = true;
      // initialize Shepherd tour...
    },

    // Keep dropdowns exclusive for current route; opens correct parent on deep links
    syncDropdownStates() {
      const path = this.$route.path || '';
      const isQa = path.startsWith('/qa') && !path.includes('/qa-assignment');
      const isClients = path.startsWith('/clients') || path.startsWith('/add-client');
      const isMembers = path.startsWith('/members');

      // Set dropdown states based on current route
      this.qaDropdownOpen = isQa;
      this.clientsDropdownOpen = isClients;
      this.membershipDropdownOpen = isMembers;
    },

    // Session management methods
    updateSessionStatus() {
      if (window.sessionManager) {
        this.sessionStatus.isActive = window.sessionManager.isSessionActive();
        this.sessionStatus.remainingTime = window.sessionManager.getRemainingTime();
      }
    },

    resetSession() {
      if (window.sessionManager) {
        window.sessionManager.resetSession();
        this.updateSessionStatus();
        this.showSnackbar('Session refreshed', 'success');
      }
    },

    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    startSessionMonitoring() {
      // Update session status every minute
      this.sessionInterval = setInterval(() => {
        this.updateSessionStatus();
      }, 60000); // Update every minute
    },

    stopSessionMonitoring() {
      if (this.sessionInterval) {
        clearInterval(this.sessionInterval);
        this.sessionInterval = null;
      }
    },
  },

  mounted() {
    this.updatePageTitle();
    this.syncDropdownStates();
    this.updateSessionStatus();
    this.startSessionMonitoring();
  },

  beforeUnmount() {
    this.stopSessionMonitoring();
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap');

* {
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
}

.avatar-text {
  font-size: 1rem;
  font-weight: 600;
  color: white;
}

.user-menu__role {
  color: #666;
  font-size: 0.9rem;
  text-transform: capitalize;
  margin: 4px 0;
}

.logout-btn {
  color: #d32f2f !important;
}

.logout-btn:hover {
  background: rgba(211, 47, 47, 0.1) !important;
}

.snackbar {
  color: #fff;
}

.session-status {
  text-align: center;
}

.session-status .v-chip {
  font-size: 0.75rem;
}

.app-bar {
  background-color: white;
}

.toolbar-title {
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  padding-bottom: 5px;
}

.drawer-background {
  background-color: #002060;
  border-right: 4px solid #BCA258;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 10px;
  height: 50px;
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.nav-title {
  display: flex;
  align-items: center;
  font-weight: 400;
  width: 100%;
}

.nav-title .icon {
  margin-right: 16px;
}

/* Slide-in animation for the newly selected item */
.selected-item {
  background-color: #BCA258;
  margin-left:-6px;
  border-radius: 0 30px 30px 0;
  animation: slide-in-selected 0.3s ease-out forwards;
}

/* Custom Law Questions Dropdown Styles */
.custom-dropdown {
  width: 100%;
}

.custom-qa-item {
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.custom-qa-item .nav-title {
  display: flex;
  align-items: center;
  font-weight: 400;
  width: 100%;
  font-size: 0.8rem;
}

.custom-qa-item .icon {
  margin-right: 16px;
}

/* Custom Clients Dropdown Styles */
.custom-clients-item {
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.custom-clients-item .nav-title {
  display: flex;
  align-items: center;
  font-weight: 400;
  width: 100%;
  font-size: 0.8rem;
}

.custom-clients-item .icon {
  margin-right: 16px;
}

/* Custom Membership Dropdown Styles */
.custom-membership-item {
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
}

.custom-membership-item .nav-title {
  display: flex;
  align-items: center;
  font-weight: 400;
  width: 100%;
  font-size: 0.8rem;
}

.custom-membership-item .icon {
  margin-right: 16px;
}

.custom-dropdown-arrow {
  position: absolute !important;
  right: 12px !important;
  font-size: 1.2rem !important;
  transition: transform 0.3s ease !important;
}

.custom-dropdown-arrow.rotated {
  transform: rotate(180deg) !important;
}

.custom-submenu {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease;
  background-color: transparent;
  border-radius: 0 0 30px 0;
}

.custom-submenu.submenu-open {
  max-height: 200px;
}

/* Sub-item styles for dropdown */
.sub-item {
  cursor: pointer;
  transition: background-color 0.3s;
  position: relative;
  background-color: transparent !important;
  padding-left: 20px !important;
}

.sub-item .nav-title {
  display: flex;
  align-items: center;
  font-weight: 400;
  width: 100%;
  font-size: 0.9rem;
}

.sub-item .icon {
  font-size: 1.2rem;
  margin-right: 12px;
}

.sub-item:hover {
  background-color: rgba(255, 255, 255, 0.1) !important;
  border-radius: 0 30px 30px 0;
}

@keyframes slide-in-selected {
  from {
    transform: translateX(-20px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.underline {
  height: 4px;
  background-color: #BCA258;
  width: 0;
  transition: width 0.3s ease-in-out;
}

.underline-active .underline {
  width: 80px;
}

/* Responsive */
@media (max-width: 600px) {
  .toolbar-title {
    font-size: 1rem;
  }
  
  .nav-title {
    font-size: 0.9rem;
  }
  
  .sub-item .nav-title {
    font-size: 0.8rem;
  }
  
  .custom-dropdown-arrow {
    right: 8px !important;
    font-size: 1rem !important;
  }
}
</style>
